import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";

import { api } from "./ApiClient";

import { AuthHelper } from "../helpers/auth.helper";

import { closeModal } from "../store/modal.slice";

import { IUsersListItemForCohortMemberSelection, ICohortsListItem, ICohortMemberListItem, HMISaveCohort, HMIDeleteCohort, HMIAddMemberToCohort, HMIRemoveMemberFromCohort, HMISaveCohortMemberEntitlements } from "../types/services.interfaces/cohorts-cax";

export const useGetCohorts = (subClientIds: string) => {
    AuthHelper.presetPrivateRequest(api);

    const queryFn = async (): Promise<ICohortsListItem[]> => {
        try {
            const res = await api.get(`/cohorts-cax/list/${subClientIds}`);
            return res.data;
        } catch (error: any) {
            toast.error(error?.response?.data?.message ?? 'Failed to load cohorts.', {
                duration: 4000,
                position: 'top-right',
            });
            return [];
        };
    };

    return useQuery({
        queryKey: ['cohorts-list', subClientIds],
        queryFn,
    });
};

export const useGetCohort = (cohortId: number | null) => {
    AuthHelper.presetPrivateRequest(api);

    const queryFn = async (): Promise<ICohortsListItem> => {
        const res = await api.get(`/cohorts-cax/details/${cohortId}`);
        return res.data;
    };

    return useQuery({
        queryKey: ['cohorts-details', cohortId],
        enabled: !!cohortId,
        queryFn,
    });
};

export const useSaveCohort = () => {
    AuthHelper.presetPrivateRequest(api);

    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    const mutationFn = async (data: HMISaveCohort): Promise<void> => {
        const res = await api.post('/cohorts-cax/save', data.payload);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsProcessing(true);
        },
        onSuccess: () => {
            toast.success('Cohort saved successfully', {
                duration: 4000,
                position: 'top-right',
            });

            dispatch(closeModal("MANAGE_COHORT_MODAL"));
        },
        onError: (error: any) => {
            toast.error(error.response.data.message, {
                duration: 4000,
                position: 'top-right',
            });
        },
        onSettled: (_, __, variables) => {
            queryClient.invalidateQueries({ queryKey: ['cohorts-list'] });
            queryClient.invalidateQueries({ queryKey: ['cohorts-details'] });
            variables.setIsProcessing(false);
        },
    });
};

export const useDeleteCohort = () => {
    AuthHelper.presetPrivateRequest(api);

    const queryClient = useQueryClient();

    const mutationFn = async (data: HMIDeleteCohort): Promise<void> => {
        const res = await api.delete(`/cohorts-cax/delete/${data.id}`);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsProcessing(true);
        },
        onSuccess: () => {
            toast.success('Cohort deleted successfully', {
                duration: 4000,
                position: 'top-right',
            });
        },
        onError: (error: any) => {
            toast.error(error.response.data.message, {
                duration: 4000,
                position: 'top-right',
            });
        },
        onSettled: (_, __, variables) => {
            queryClient.invalidateQueries({ queryKey: ['cohorts-list'] });
            variables.setIsProcessing(false);
        },
    });
};

export const useGetCohortMembers = (cohortId: number | null) => {
    AuthHelper.presetPrivateRequest(api);

    const queryFn = async (): Promise<ICohortMemberListItem[]> => {
        const res = await api.get(`/cohorts-cax/cohort-members/${cohortId}`);
        return res.data;
    };

    return useQuery({
        queryKey: ['cohort-members', cohortId],
        enabled: !!cohortId,
        queryFn,
    });
};

export const useGetUsersListForCohortMemberSelection = (cohortId: number | null) => {
    AuthHelper.presetPrivateRequest(api);

    const queryFn = async (): Promise<IUsersListItemForCohortMemberSelection[]> => {
        const res = await api.get(`/cohorts-cax/users-list-for-cohort-member-selection/${cohortId}`);
        return res.data;
    };

    return useQuery({
        queryKey: ['users-list-for-cohort-member-selection', cohortId],
        enabled: !!cohortId,
        queryFn,
    });
};

export const useAddMemberToCohort = () => {
    AuthHelper.presetPrivateRequest(api);

    const queryClient = useQueryClient();

    const mutationFn = async (data: HMIAddMemberToCohort): Promise<void> => {
        const res = await api.put(`/cohorts-cax/add-member/${data.userId}/${data.cohortId}`);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsProcessing(true);
        },
        onSuccess: () => {
            toast.success('Member added to cohort successfully', {
                duration: 4000,
                position: 'top-right',
            });
        },
        onError: (error: any) => {
            toast.error(error.response.data.message, {
                duration: 4000,
                position: 'top-right',
            });
        },
        onSettled: (_, __, variables) => {
            queryClient.invalidateQueries({ queryKey: ['cohort-members'] });
            queryClient.invalidateQueries({ queryKey: ['users-list-for-cohort-member-selection'] });
            queryClient.invalidateQueries({ queryKey: ['cohorts-list'] });
            variables.setIsProcessing(false);
        },
    });
};

export const useRemoveMemberFromCohort = () => {
    AuthHelper.presetPrivateRequest(api);

    const queryClient = useQueryClient();

    const mutationFn = async (data: HMIRemoveMemberFromCohort): Promise<void> => {
        const res = await api.put(`/cohorts-cax/remove-member/${data.userId}/${data.cohortId}`);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsProcessing(true);
        },
        onSuccess: () => {
            toast.success('Member removed from cohort successfully', {
                duration: 4000,
                position: 'top-right',
            });
        },
        onError: (error: any) => {
            toast.error(error.response.data.message, {
                duration: 4000,
                position: 'top-right',
            });
        },
        onSettled: (_, __, variables) => {
            queryClient.invalidateQueries({ queryKey: ['cohort-members'] });
            queryClient.invalidateQueries({ queryKey: ['users-list-for-cohort-member-selection'] });
            queryClient.invalidateQueries({ queryKey: ['cohorts-list'] });
            variables.setIsProcessing(false);
        },
    });
};

export const useSaveCohortMemberEntitlements = () => {
    AuthHelper.presetPrivateRequest(api);

    const queryClient = useQueryClient();

    const mutationFn = async (data: HMISaveCohortMemberEntitlements): Promise<void> => {
        const res = await api.post(`/cohorts-cax/save-cohort-member-entitlements/${data.userId}/${data.cohortId}`, data.payload);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setProcessingUserId(variables.userId);
        },
        onSuccess: () => {
            toast.success('Member entitlements saved successfully', {
                duration: 4000,
                position: 'top-right',
            });
        },
        onError: (error: any) => {
            toast.error(error.response.data.message, {
                duration: 4000,
                position: 'top-right',
            });
        },
        onSettled: (_, __, variables) => {
            queryClient.invalidateQueries({ queryKey: ['cohort-members'] });
            queryClient.invalidateQueries({ queryKey: ['cohorts-list'] });
            queryClient.invalidateQueries({ queryKey: ['cohorts-details'] });
            queryClient.invalidateQueries({ queryKey: ['entitlements-for-admin-form'] });
            variables.setProcessingUserId(null);
        },
    });
};
